import * as moment from 'moment';
import { teamName } from './team-name';

/**
 * The patient messages a booking carries, rebuilt for a new date and time. The server emails the
 * text stored on the booking, with the date already written in, so a move has to send new text.
 */

export interface MessagePlaceholderValues {
  patientName?: string;
  groupName?: string;
  appointmentDateTime?: string;
  reminderMessage?: string;
  starveTime?: string;
  arriveTime?: string;
  arriveLocation?: string;
}

export interface AppointmentMessages {
  confirmation: string;
  reschedule: string;
  cancellation: string;
  reminder: string;
  isProcedure: boolean;
}

/** Fills the {{...}} placeholders of an appointment message template. */
export function fillMessagePlaceholders(template: string, values: MessagePlaceholderValues): string {
  const replacements: { [key: string]: string } = {
    '{{patientName}}': values.patientName || '',
    '{{groupName}}': values.groupName || '',
    '{{appointmentTime}}': values.appointmentDateTime || '',
    '{{appointmentDateTime}}': values.appointmentDateTime || '',
    '{{newAppointmentTime}}': values.appointmentDateTime || '',
    '{{reminderMessage}}': values.reminderMessage || '',
    '{{starveTime}}': values.starveTime || '',
    '{{arriveTime}}': values.arriveTime || '',
    '{{arriveLocation}}': values.arriveLocation || '',
  };
  // "The {{groupName}} Team" for a practice named "The ..." would read "The The ... Team".
  template = template.replace(/The \{\{groupName\}\} Team/g, `The ${teamName(values.groupName)} Team`);
  Object.keys(replacements).forEach(placeholder => {
    template = template.replace(new RegExp(placeholder, 'g'), replacements[placeholder]);
  });
  return template.replace('??', '👋');
}

/** "06:00" from a stored starve / arrive time, whether it holds a clock time or a full date. */
function clockTime(value: any): string {
  if (!value) return '';
  if (/^\d{2}:\d{2}/.test(String(value))) return String(value).slice(0, 5);
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format('HH:mm') : '';
}

/**
 * The booking's messages for its new `date` (YYYY-MM-DD) and `timeSlot`, the way the edit form
 * builds them: stored text has its "YYYY-MM-DD at HH:mm" moved to the new slot; without stored
 * text the type's template is filled in.
 */
export function rescheduledMessages(
  appointment: any,
  appointmentType: any,
  values: { patientName: string; groupName: string; date: string; timeSlot: string },
): AppointmentMessages {
  const appointmentDateTime = `${values.date} at ${values.timeSlot}`;
  const dateTime = /\d{4}-\d{2}-\d{2} at \d{2}:\d{2}/g;
  const fill = (stored: string | undefined, fallback: string | undefined) => fillMessagePlaceholders(
    (stored || '').replace(dateTime, appointmentDateTime) || fallback || '',
    {
      patientName: values.patientName,
      groupName: values.groupName,
      appointmentDateTime,
      reminderMessage: appointmentType?.reminderMessage,
      starveTime: clockTime(appointment?.starveTime),
      arriveTime: clockTime(appointment?.arriveTime),
      arriveLocation: appointmentType?.arriveLocation || 'The Treatment Centre',
    });
  return {
    confirmation: fill(appointment?.confirmationMessage, appointmentType?.confirmationMessage),
    reschedule: fill(appointment?.rescheduleMessage, appointmentType?.rescheduleMessage),
    cancellation: fill(appointment?.cancellationMessage, appointmentType?.cancellationMessage),
    reminder: fill(appointment?.autoReminderMessage, appointmentType?.autoReminderMessage),
    isProcedure: !!(appointment?.isProcedure ?? appointmentType?.isProcedure),
  };
}

/** The booking's new date, start and end, keeping its length; no stored end stays without one. */
export function movedBooking(appointment: any, date: string, timeSlot: string): { date: string; timeSlot: string; endSlot: string | null } {
  let endSlot: string | null = null;
  if (appointment?.endSlot && appointment?.timeSlot) {
    const minutes = moment(appointment.endSlot, 'HH:mm').diff(moment(appointment.timeSlot, 'HH:mm'), 'minutes');
    endSlot = moment(timeSlot, 'HH:mm').add(minutes, 'minutes').format('HH:mm');
  }
  return { date, timeSlot, endSlot };
}
